import React from 'react'
import styled from 'styled-components'

import { ColumnFlex } from '~/components/common/containers'
import { LANDING_PAGE_WIDTH, MIN_DESKTOP_THRESHOLD } from '~/core/constants'
import { translate } from '~/core/translate'
import { LabelRegular, bodyShort02Css, TitleH7 } from '../typography'
import Delimiter from '~/components/common/delimiter'
import MauTooltip from './mau-tooltip'

const Section = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
`

const OverageText = styled(LabelRegular)`
  display: block;
  margin: 0 16px 16px;
  text-align: center;

  div {
    display: inline-block;
  }
`

const FreePlanSection = styled(ColumnFlex)`
  padding: 0 24px;
  text-align: center;

  ${TitleH7} {
    margin-bottom: 16px;
  }
`

const TaxesText = styled(LabelRegular)`
  margin: 0 16px;
  text-align: center;
`

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 8px;
  margin: 0 16px;
`

const Header = styled.div`
  ${bodyShort02Css}
  margin: 0 auto 16px;
`

const Container = styled(ColumnFlex)`
  background-color: #F2F3F4;
  border-radius: 8px;
  box-sizing: border-box;
  margin: 8px 24px 0;
  padding: 16px 0;
  width: calc(100% - 48px);

  ${Delimiter} {
    border-color: #C4C4C4;
    margin: 16px 0;
  }

  @media screen and (min-width: ${MIN_DESKTOP_THRESHOLD}) {
    width: ${LANDING_PAGE_WIDTH};
  }

  @media screen and (min-width: ${MIN_DESKTOP_THRESHOLD}) {
    text-align: center;
    align-items: center;

    ${Content} {
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 16px;
      grid-row-gap: 16px;
      text-align: center;

      ${Section} {
        display: block;
      }
    }
  }
`

const Addons = ({ products = [], className, noCreditCard }) => {
  const { overages } = products[0] || {}
  const freePlan = products.find(p => p.totalCost === 0)

  if (!overages) {
    return null
  }

  const {
    dialOut,
    mau,
    recording,
    sipInbound,
    streaming,
    transcription
  } = overages

  return (
    <Container className={className}>
      {noCreditCard && (
        <>
          <FreePlanSection>
            <TitleH7>You can add your credit card details later to unlock extra usage and premium features</TitleH7>
            <LabelRegular>{`The free plan includes ${freePlan.users} monthly active users (MAU). If you exceed this limit, you won’t be able to use the service unless you will add a payment option to the free plan or you will upgrade to a paid plan. We will apply an overage charge of $${mau} per MAU.`}</LabelRegular>
          </FreePlanSection>
          <Delimiter isFullLength />
        </>
      )}

      <Header>Premium features</Header>
      <Content>
        {recording && (
          <Section>
            <LabelRegular>{translate('recording')}:&nbsp;</LabelRegular>
            <LabelRegular>${recording} / minute</LabelRegular>
          </Section>
        )}

        {streaming && (
          <Section>
            <LabelRegular>{translate('rtmp_streaming')}:&nbsp;</LabelRegular>
            <LabelRegular>${streaming} / minute</LabelRegular>
          </Section>
        )}

        {transcription && (
          <Section>
            <LabelRegular>{translate('meeting_transcript_short')}:&nbsp;</LabelRegular>
            <LabelRegular>${transcription} / minute</LabelRegular>
          </Section>
        )}

        {dialOut && (
          <Section>
            <LabelRegular>PSTN Outbound:&nbsp;</LabelRegular>
            <LabelRegular>1 MAU + ${dialOut} / minute</LabelRegular>
          </Section>
        )}

        <Section>
          <LabelRegular>Dialling into a meeting using a PSTN would be counted as 2 MAU</LabelRegular>
        </Section>

        {sipInbound && (
          <Section>
            <LabelRegular>SIP in/out:&nbsp;</LabelRegular>
            <LabelRegular>${sipInbound} / minute</LabelRegular>
          </Section>
        )}
      </Content>
      <Delimiter isFullLength />
      {!noCreditCard && (
        <OverageText>
          Your plan includes a fixed number of&nbsp;<MauTooltip><u>monthly active users (MAU).</u></MauTooltip>&nbsp;If you exceed your included&nbsp;<MauTooltip />, {`we will apply an overage charge of ${mau} per`}&nbsp;<MauTooltip />.
        </OverageText>
      )}
      <TaxesText>{translate('taxes_excluded_from_price')}</TaxesText>
    </Container>
  )
}

export default styled(Addons)``
